import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import type { GroupComparison } from '@/api/statistics';
import { useChartTheme } from '@/hooks/useChartTheme';
import { useTranslation } from 'react-i18next';

interface GroupComparisonChartProps {
  data: GroupComparison[];
  currency: string;
}

function CustomTooltip({ active, payload, label, currency }: any) {
  if (!active || !payload?.length) return null;
  const minorUnits = getMinorUnitsSync(currency);
  return (
    <div className="rounded-lg bg-surface-primary px-3 py-2 shadow-lg ring-1 ring-border-primary">
      <p className="text-xs font-medium text-text-secondary">{label}</p>
      <p className="text-sm font-semibold text-text-primary">
        {formatMinorAmount(payload[0].value, currency, minorUnits)}
      </p>
    </div>
  );
}

export function GroupComparisonChart({ data, currency }: GroupComparisonChartProps) {
  const { t } = useTranslation();
  const minorUnits = getMinorUnitsSync(currency);
  const ct = useChartTheme();
  const formatTick = (value: number) =>
    formatMinorAmount(value, currency, minorUnits, undefined, true);

  if (data.length <= 1) return null;

  const chartData = data.map(g => ({
    name: g.name,
    totalSpent: g.totalSpent,
    color: g.color,
  }));

  return (
    <div className="rounded-xl bg-surface-primary p-5 shadow-sm ring-1 ring-border-primary">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{t('web:groupComparisonChart.groupComparison')}</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 50)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 20, bottom: 5, left: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: ct.tick }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatTick}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12, fill: ct.tickSecondary }}
            axisLine={false}
            tickLine={false}
            width={120}
          />
          <Tooltip content={<CustomTooltip currency={currency} />} cursor={{ fill: ct.cursorFill }} />
          <Bar dataKey="totalSpent" radius={[0, 4, 4, 0]} barSize={20}>
            {chartData.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
